import type { ReactNode } from 'react'
import { getScreenState } from '../../app-state/state.screen'
import type { StatsData } from '../../components/presentation/Modals/Drawers/MyAccount/TokenStats'
import APP_BREAKPOINTS from '../../components/styles/breakpoints'
import {
    COMPONENT_HEIGHTS,
    COMPONENT_WIDTHS,
    getBrowserBottomInset,
} from '../../components/theme'
import { NOOP } from '../../constants'
import { ConfirmationType } from '../../constants/enums'
import {
    FLOATING_PREVIEW,
    FLOATING_ROOM,
    FloaterPageState,
    FloaterSize,
    MY_WEBCAM,
    QUICK_REPLY,
} from '../../constants/floaters'
import type { NewsListUser } from '../../libraries/mfc/MfcJsTypes'
import { IUserImage } from '../../mfcLogic/api/chatImages'
import { MergedPrivateGalleryUser } from '../../mfcLogic/api/privateGallery'
import type { ITokenUsageResult } from '../../mfcLogic/api/tokenUsage'
import type { TokenPurchasePanes } from '../../mfcLogic/tokens'
import { isTouchDevice } from '../../utilities/browser'
import { safeObj } from '../../utilities/safe'
import { pubSubModal } from '../pubSub'
import { APPSTATE } from '../state.app'
import { LAYOUTSTATE } from '../state.layout'
import { Box } from '../state.mail'

/// THIS FILE MOSTLY EXISTS TO AVOID CIRCULAR IMPORTS FROM `state.screen.ts` AND `state.floater.ts`

export enum ModalEvent {
    UPDATE_DIALOG = 'update_dialog',
    UPDATE_DRAWER = 'update_drawer',
    UPDATE_CONFIRMATION = 'update_confirmation',
    UPDATE_FLOATER = 'update_floater',
    UPDATE_FLOATING_ROOM = 'update_floating_room',
    UPDATE_QUICK_MENU = 'update_quick_menu',
    UPDATE_MODAL_STACK = 'update_modal_stack',
}
export enum ModalKind {
    Dialog = 'dialog',
    Drawer = 'drawer',
    Confirmation = 'confirmation',
    Floater = 'floater',
    FloatingRoom = 'floating_room',
    QuickMenu = 'quick_menu',
}

export type FloatingRoomFloaterState = {
    id: number // room id
    expanded: boolean // whether or not the room should be full width
    minimized: boolean // whether or not video is toggled (pm/profile)
    size: FloaterSize
    roomView: RoomView
    locations: FloaterLocations
}
export enum RoomView {
    'Room' = 0,
    'PM' = 1,
    'Profile' = 2,
}

export type BaseModal = {
    type: string
    show: boolean
    state: any
}

export interface FloatingRoomModal extends BaseModal {
    state: FloatingRoomFloaterState
}

export type QuickMenuState = {
    id: number
    position: Position
    elevated: boolean
}

export interface QuickMenuModal extends BaseModal {
    state: QuickMenuState
}

export type FloaterState = {
    id?: number
    minimized?: boolean
    locations?: FloaterLocations
    size?: FloaterSize
    sticky?: boolean
    stickyHeader?: boolean
    close?: boolean
}

export interface FloaterModal extends BaseModal {
    state: FloaterState
}

export type FloaterModals = {
    [key: string]: FloaterModal
}

export type ConfirmationState = {
    message?: ReactNode
    title?: string
    cancel?: boolean
    onCancel?(e?: any): any
    ok?: boolean
    okText?: string
    onOk?(e?: any): any
}

export interface ConfirmationModal extends BaseModal {
    state: ConfirmationState
}

export type DrawerState = {
    title?: string | false
    hideTitle?: boolean
    showAvatar?: boolean
    subject?: string
    stickyHeader?: boolean
    maxHeight?: boolean
    halfHeight?: boolean
    redirect?: string
    onBack?(): void
    listType?: string
    list?: NewsListUser[]
    slug?: string
    name?: string
    id?: number
    identifier?: string
    slugOrId?: string | number
    drawer?: boolean
    confirmationType?: ConfirmationType
    to?: string
    box?: Box
    mailId?: number
    section?: string
    thing?: string
    thing_type?: string
    anonymousTip?: boolean
    chatError?: boolean
    hiddenShows?: boolean
    pinned?: boolean
    priorComment?: string
    priorValue?: number
    publicComment?: boolean
    publicTip?: boolean
    queue?: TokenPurchasePanes[]
    product?: string
    type?: string
    amount?: number
    processor?: string
    modelList?: string[]
    statsData?: StatsData
    tokenUsageHistory?: ITokenUsageResult
    message?: string
}

export interface DrawerModal extends BaseModal {
    state: DrawerState
}

export type DialogState = {
    disableBackdropClick?: boolean
    expanded?: boolean
    close?: boolean
    slug?: string
    identifier?: string | number
    fullHeight?: boolean
    hideNextPrev?: boolean
    message?: boolean | string
    noVideo?: boolean
    room?: boolean
    onInsertEmoticon?(key: string): void
    onInsertImage?(imageData: IUserImage): void
    showSearch?: boolean
    fullWindow?: boolean
    currentPosition?: number
    fullPage?: boolean
    gallery?: MergedPrivateGalleryUser
    galleries?: Array<MergedPrivateGalleryUser>
    title?: string
}

export interface DialogModal extends BaseModal {
    state: DialogState
}

export type ModalState = {
    [ModalKind.Confirmation]: ConfirmationModal
    [ModalKind.Dialog]: DialogModal
    [ModalKind.Drawer]: DrawerModal
    [ModalKind.Floater]: FloaterModals
    [ModalKind.FloatingRoom]: FloatingRoomModal
    [ModalKind.QuickMenu]: QuickMenuModal
}
//Record<ModalKind, Modal | MultiModal>

/// swap between fullscreen or content area sizes for the floating room
export const setFullScreenExpandedState = (fullScreen: boolean) => {
    const floatingRoom = MODALSTATE.floating_room as FloatingRoomModal
    const { portrait } = getScreenState()
    const fullWindow = LAYOUTSTATE.layout.fullWindow
    const newExpanded =
        fullWindow || APPSTATE.app.isMobile || portrait
            ? true
            : (fullScreen ?? floatingRoom.state.expanded)

    const updatedRoomState = {
        ...floatingRoom.state,
        expanded: newExpanded,
    }
    setModal(
        ModalKind.FloatingRoom,
        FLOATING_ROOM,
        updatedRoomState,
        floatingRoom.show,
        true,
    )
}

export const QUICK_REPLY_WIDTH = 320
export const QUICK_REPLY_MOBILE_WIDTH = 260
export const QUICK_REPLY_CHAT_HEIGHT = 260
export const QUICK_REPLY_MOBILE_CHAT_HEIGHT = 190
export const QUICK_REPLY_HEIGHT =
    COMPONENT_HEIGHTS.chatHeader + 40 + QUICK_REPLY_CHAT_HEIGHT
export const QUICK_REPLY_MOBILE_HEIGHT =
    COMPONENT_HEIGHTS.chatHeader + 40 + QUICK_REPLY_MOBILE_CHAT_HEIGHT
const MY_WEBCAM_VIDEO_WIDTH = 320
const MY_WEBCAM_VIDEO_HEIGHT = 180
const MY_WEBCAM_TOP_SECTION_HEIGHT = 90
const MY_WEBCAM_BOTTOM_SECTION_HEIGHT = 50
const MY_WEBCAM_HEIGHT_LARGE =
    MY_WEBCAM_TOP_SECTION_HEIGHT +
    MY_WEBCAM_VIDEO_HEIGHT +
    MY_WEBCAM_BOTTOM_SECTION_HEIGHT +
    16
const MY_WEBCAM_HEIGHT_MEDIUM =
    MY_WEBCAM_VIDEO_HEIGHT + MY_WEBCAM_BOTTOM_SECTION_HEIGHT + 16
const MY_WEBCAM_HEIGHT_SMALL = MY_WEBCAM_VIDEO_HEIGHT

const MY_WEBCAM_WIDTHS = {
    [FloaterSize.Small]: MY_WEBCAM_VIDEO_WIDTH,
    [FloaterSize.Medium]: MY_WEBCAM_VIDEO_WIDTH + 16,
    [FloaterSize.Large]: MY_WEBCAM_VIDEO_WIDTH + 16,
}
const MY_WEBCAM_HEIGHTS = {
    [FloaterSize.Small]: MY_WEBCAM_HEIGHT_SMALL,
    [FloaterSize.Medium]: MY_WEBCAM_HEIGHT_MEDIUM,
    [FloaterSize.Large]: MY_WEBCAM_HEIGHT_LARGE,
}

export const PREVIEW_SIZES = {
    [FloaterSize.Small]: () =>
        Math.min(Math.max(getScreenState().windowWidth * 0.4, 180), 225),
    [FloaterSize.Medium]: () =>
        (Math.max((getScreenState().windowWidth || 800) / 2 - 50 * 2, 300) -
            200) /
            2 +
        200,
    [FloaterSize.Large]: () =>
        Math.max((getScreenState().windowWidth || 800) / 2 - 50 * 2, 300),
    [FloaterSize.Fullscreen]: () => {
        return getScreenState().contentWidth
    },
}

export const closeFloatingPreview = (id: number) => {
    const modalKey = generateModalKey(FLOATING_PREVIEW, id)
    const { [modalKey]: _, ...floaters } = MODALSTATE.floater
    emitModalUpdate(ModalKind.Floater, floaters, true)
    setModalStack(modalKey, { deleteKey: true })
}
export const getNewExpandLocation = (
    modalKey: string,
    type: string,
    proposedSize?: FloaterSize,
    proposedLocation?: FloaterLocation,
): [newLocation: FloaterLocation, changed: boolean] => {
    const floaterState = safeObj(
        (type === FLOATING_ROOM
            ? MODALSTATE.floating_room
            : MODALSTATE.floater[modalKey]
        )?.state,
    )
    const {
        locations: foundLocations = {},
        // close = true,
        size: existingSize,
    } = floaterState
    const size = proposedSize ?? existingSize
    const [orientation, portrait] = getFloaterOrientation()
    const currentLocation = proposedLocation
        ? { ...proposedLocation }
        : foundLocations[orientation]
    const { windowHeight, windowWidth } = getScreenState()
    // eslint-disable-next-line fp/no-let
    let changed = false
    // check to make sure the floater isnt off screen somehow
    // if screen size is <600 px then center it upon de-minimize
    if (type === QUICK_REPLY) {
        // off the bottom
        if (currentLocation.y + QUICK_REPLY_HEIGHT > windowHeight) {
            currentLocation.y = windowHeight - (QUICK_REPLY_HEIGHT + 24)
            changed = true
        }
        // too close to the top
        if (currentLocation.y < 8) {
            currentLocation.y = 8
            changed = true
        }
        // too close to the left side
        if (currentLocation.x < 8) {
            currentLocation.x = 8
            changed = true
        }
        if (currentLocation.x + QUICK_REPLY_WIDTH + 8 >= windowWidth) {
            currentLocation.x = windowWidth - (QUICK_REPLY_WIDTH + 8)
            changed = true
        }
        // just ensure if the current x + QUICK_REPLY_WIDTH > W
        return [{ ...currentLocation }, changed]
    } else if (type === MY_WEBCAM) {
        const bottomOffset = portrait ? COMPONENT_HEIGHTS.navBottom + 8 : 24
        // off the bottom
        if (
            currentLocation.y + MY_WEBCAM_HEIGHTS[size] + bottomOffset >
            windowHeight
        ) {
            currentLocation.y =
                windowHeight - (MY_WEBCAM_HEIGHTS[size] + bottomOffset)
            changed = true
        }
        // too close to the top
        if (currentLocation.y < 8) {
            currentLocation.y = 8
            changed = true
        }
        // too close to the left side
        if (currentLocation.x < 8) {
            currentLocation.x = 8
            changed = true
        }
        if (currentLocation.x + MY_WEBCAM_WIDTHS[size] + 36 > windowWidth) {
            currentLocation.x = windowWidth - (MY_WEBCAM_WIDTHS[size] + 36)
            changed = true
        }
        return [{ ...currentLocation }, changed]
    } else if (
        type === FLOATING_PREVIEW ||
        (type === FLOATING_ROOM && size < FloaterSize.Fullscreen)
    ) {
        // check the current size to find the dimension boundaries to check
        const compactHeader =
            windowWidth < APP_BREAKPOINTS.md || APPSTATE.app.isMobile
        const { expandedSearchMobile } = LAYOUTSTATE.layout
        const headerHeight =
            (compactHeader
                ? COMPONENT_HEIGHTS.headerCompact
                : COMPONENT_HEIGHTS.headerDesktop) +
            (expandedSearchMobile ? COMPONENT_HEIGHTS.inputSearch + 8 : 0)

        const calcWidth = PREVIEW_SIZES[size]()
        const videoHeight = (calcWidth / 16) * 9
        const newPreviewHeight =
            videoHeight +
            (size >= FloaterSize.Medium ? COMPONENT_HEIGHTS.chatHeader : 0)

        const usableWindowHeight = portrait
            ? windowHeight -
              (headerHeight +
                  COMPONENT_HEIGHTS.navBottom +
                  getBrowserBottomInset())
            : windowHeight - headerHeight
        const usableWindowWidth = portrait
            ? windowWidth
            : windowWidth - COMPONENT_WIDTHS.leftNav

        // off the bottom
        if (typeof currentLocation.y === 'string') {
            const percentage = parseInt(currentLocation.y.split('%')[0], 10)
            if (percentage) {
                const calcY = (percentage / 100) * windowHeight

                if (calcY + newPreviewHeight > usableWindowHeight) {
                    const adjustedY =
                        usableWindowHeight - (newPreviewHeight + 8)
                    const newYPercentage = Math.floor(
                        (adjustedY / windowHeight) * 100,
                    )
                    currentLocation.y = `${newYPercentage}%`
                    changed = true
                }
            }
        }
        if (currentLocation.y + newPreviewHeight > usableWindowHeight) {
            currentLocation.y = usableWindowHeight - (newPreviewHeight + 8)
            changed = true
        }
        // too close to the top
        if (currentLocation.y < 8) {
            currentLocation.y = 8
            changed = true
        }
        // too close to the left side
        if (portrait) {
            if (currentLocation.x < 8) {
                currentLocation.x = 8
                changed = true
            }
        } else {
            if (LAYOUTSTATE.layout.expandedNav) {
                if (currentLocation.x < COMPONENT_WIDTHS.leftNavExpanded) {
                    currentLocation.x = COMPONENT_WIDTHS.leftNavExpanded
                    changed = true
                }
            } else {
                if (currentLocation.x < COMPONENT_WIDTHS.leftNav) {
                    currentLocation.x = COMPONENT_WIDTHS.leftNav
                    changed = true
                }
            }
        }
        const { close = true } = floaterState as FloaterState
        // too close to right side
        if (currentLocation.x + calcWidth + (close ? 36 : 8) > windowWidth) {
            currentLocation.x =
                usableWindowWidth - (calcWidth + (close ? 36 : 8))
            changed = true
        }
        return [
            {
                ...currentLocation,
                width: calcWidth,
                height: newPreviewHeight,
            },
            changed,
        ]
    }
    return [currentLocation, changed]
}

export const repositionFloater = (
    floater: FloaterModal | FloatingRoomModal,
) => {
    const isFloatingRoom = floater.type === FLOATING_ROOM
    const shouldReposition =
        !isFloatingRoom ||
        (isFloatingRoom && floater.state.size < FloaterSize.Fullscreen)
    if (floater.show && shouldReposition) {
        // 2) check if they're offscreen
        const modalKey = generateModalKey(floater.type, floater.state.id)
        const [newLocation, changed] = getNewExpandLocation(
            modalKey,
            floater.type,
        )
        if (changed) {
            // 3) reposition as needed (only emit if we've updated location)
            const [orientation] = getFloaterOrientation()
            if (isFloatingRoom) {
                setModal(
                    ModalKind.FloatingRoom,
                    floater.type,
                    {
                        id: floater.state.id,
                        expanded: (floater as FloatingRoomModal).state.expanded,
                        minimized: floater.state.minimized,
                        size: floater.state.size,
                        roomView: (floater as FloatingRoomModal).state.roomView,
                        locations: {
                            ...floater.state.locations,
                            [orientation]: newLocation,
                        },
                    },
                    floater.show,
                )
            } else
                setMultiModal(
                    modalKey,
                    floater.type,
                    {
                        ...floater.state,
                        locations: {
                            ...floater.state.locations,
                            [orientation]: newLocation,
                        },
                    },
                    floater.show,
                )
        }
    }
}
export const getFloaterOrientation = (): [
    orientation: 'portrait' | 'landscape',
    isPortrait: boolean,
] => {
    const { portrait } = getScreenState()
    const isMobile = APPSTATE.app.isMobile || isTouchDevice
    if (!isMobile) return ['landscape', false]
    return [portrait ? 'portrait' : 'landscape', portrait]
}

export const DEFAULT_FLOATING_ROOM: FloatingRoomModal = {
    type: FLOATING_ROOM,
    show: false,
    state: {
        id: null,
        expanded: true,
        minimized: false,
        size: FloaterSize.Fullscreen,
        roomView: RoomView.Room,
        locations: {
            portrait: null, //initialLocation,
            landscape: null, //{ ...initialLocation },
        },
    },
}

export const MODALSTATE: ModalState = {
    [ModalKind.Dialog]: {
        state: {} as DialogState,
        type: NOOP,
        show: false,
    },
    [ModalKind.Drawer]: {
        state: {} as DrawerState,
        type: NOOP,
        show: false,
    },
    [ModalKind.Confirmation]: {
        state: {} as ConfirmationState,
        type: NOOP,
        show: false,
    },
    [ModalKind.QuickMenu]: {
        state: {} as QuickMenuState,
        type: NOOP,
        show: false,
    },
    [ModalKind.Floater]: {
        /// Multi Modal, keyed by `${id}-${QUICK_REPLY}` or other floater constants
    },
    [ModalKind.FloatingRoom]: DEFAULT_FLOATING_ROOM,
}

const modalEventMap = {
    [ModalKind.Dialog]: ModalEvent.UPDATE_DIALOG,
    [ModalKind.Drawer]: ModalEvent.UPDATE_DRAWER,
    [ModalKind.Confirmation]: ModalEvent.UPDATE_CONFIRMATION,
    [ModalKind.Floater]: ModalEvent.UPDATE_FLOATER,
    [ModalKind.FloatingRoom]: ModalEvent.UPDATE_FLOATING_ROOM,
    [ModalKind.QuickMenu]: ModalEvent.UPDATE_QUICK_MENU,
}

// the stack order for active modals
export const MODAL_STACK = {
    byPosition: [] as Array<string>, //[modalKey] // position of modalKey determines order
    byKey: {}, // [modalKey]: order
}

export const setModalStack = (
    modalKey: string,
    {
        pushDown = false,
        immediate = false,
        deleteKey = false,
    }: {
        pushDown?: boolean
        immediate?: boolean
        deleteKey?: boolean
    } = {},
) => {
    const currentPosition = MODAL_STACK.byKey[modalKey]
    // find and remove the current position of the modalKey
    if (currentPosition !== undefined && currentPosition >= 0) {
        MODAL_STACK.byPosition.splice(currentPosition, 1)
    }
    // if we're not just deleting, then add the modalKey where we need it
    if (!deleteKey) {
        if (pushDown) MODAL_STACK.byPosition.push(modalKey)
        else MODAL_STACK.byPosition = [modalKey].concat(MODAL_STACK.byPosition)
    }
    // recreate the byKey object
    MODAL_STACK.byKey = {}
    MODAL_STACK.byPosition.forEach(
        (key, position) => (MODAL_STACK.byKey[key] = position),
    )

    pubSubModal.emit(ModalEvent.UPDATE_MODAL_STACK, null, immediate)
}

export const generateModalKey = (type: string, id?: number) => {
    if (type === MY_WEBCAM) return type
    if (type === FLOATING_ROOM) return type
    return `${id}-${type}`
}

export const emitModalUpdate = <Kind extends ModalKind>(
    modalKind: Kind,
    modal: ModalState[Kind], // Modal | MultiModal | FloatingRoomFloater,
    immediate = true,
) => {
    // console.log('[emitModalUpdate]', { modalKind, modal, immediate })
    MODALSTATE[modalKind] = modal
    pubSubModal.emit(modalEventMap[modalKind], modal, immediate)
}

export type FloaterLocations = {
    [orientation: string]: FloaterLocation
}

export type FloaterLocation = {
    x?: any // number | string
    y?: any // number | string
    width?: any // number | string
    height?: any // number | string
    speedX?: any // number | string
    speedY?: any // number | string
    state?: FloaterPageState
}

export type Position = {
    x: number
    y: number
}

export const setMultiModal = (
    key: string,
    modalType: string,
    modalState?: FloaterState,
    showModal = false,
    immediate = false,
) => {
    setModalStack(key, { deleteKey: !showModal })
    emitModalUpdate(
        ModalKind.Floater,
        {
            ...MODALSTATE[ModalKind.Floater],
            [key]: {
                state: modalState,
                type: modalType,
                show: showModal,
            },
        },
        immediate,
    )
}

export const setModal = <
    Kind extends
        | ModalKind.Confirmation
        | ModalKind.Dialog
        | ModalKind.Drawer
        | ModalKind.FloatingRoom
        | ModalKind.QuickMenu,
>(
    modalKind: Kind,
    modalType: string,
    modalState: ModalState[Kind]['state'],
    showModal = false,
    immediate = false,
) => {
    setModalStack(modalType, { deleteKey: !showModal })
    const modal = {
        state: modalState,
        type: modalType,
        show: showModal,
    }
    emitModalUpdate(modalKind, modal as ModalState[Kind], immediate)
}

export const setShowModal = (
    modalKind:
        | ModalKind.Confirmation
        | ModalKind.Dialog
        | ModalKind.Drawer
        | ModalKind.QuickMenu,
    showModal: boolean,
    immediate = false,
) => {
    const existingModal = MODALSTATE[modalKind]
    setModalStack(existingModal.type, { deleteKey: !showModal })
    const modal = { ...existingModal, show: showModal }
    emitModalUpdate(modalKind, modal, immediate)
}
export const setShowMultiModal = (
    key: string,
    showModal: boolean,
    immediate = false,
) => {
    setModalStack(key, { deleteKey: !showModal })
    const existing = MODALSTATE.floater[key]
    // only set show status if we have a floater that exists
    if (existing) {
        const modal = {
            ...MODALSTATE.floater,
            [key]: {
                ...existing,
                show: showModal,
            },
        }
        emitModalUpdate(ModalKind.Floater, modal, immediate)
    }
}

///
/// Window object setup
///
if (typeof window !== 'undefined') {
    window.__MFC_APP_MODAL__ = MODALSTATE
    window.__MFC_APP_MODAL_STACK__ = MODAL_STACK
}
